import React from 'react';
import { GlassCard } from '../../../_shared/GlassCard';
import { Instagram, Youtube, Music2 } from 'lucide-react';
import { MAIN, WINDOW, formatNum } from '../data';

const PlatformIcon = ({ platform, size = 20 }) => {
    if (platform === 'Instagram') return <Instagram size={size} />;
    if (platform === 'YouTube') return <Youtube size={size} />;
    if (platform === 'TikTok') return <Music2 size={size} />;
    return null;
};

const platformAccent = (platform) => {
    if (platform === 'Instagram') return 'bg-pink-500/20 text-pink-300';
    if (platform === 'YouTube') return 'bg-red-500/20 text-red-300';
    if (platform === 'TikTok') return 'bg-cyan-500/20 text-cyan-300';
    return 'bg-white/10 text-white/70';
};

const PlatformCard = ({ entry, followerLabel = 'Followers' }) => {
    const isPending = entry.status === 'pending';
    const followerCount = entry.followers ?? entry.subscribers;

    return (
        <GlassCard className={isPending ? 'opacity-70' : ''}>
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className={`${platformAccent(entry.platform)} p-3 rounded-xl inline-flex`}>
                        <PlatformIcon platform={entry.platform} />
                    </div>
                    <div>
                        <p className="text-white font-bold text-lg leading-tight">{entry.platform}</p>
                        <p className="text-white/40 text-xs font-mono">{entry.handle}</p>
                    </div>
                </div>
                {isPending && (
                    <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 text-xs font-bold px-2.5 py-1 rounded-full">
                        Data pending
                    </span>
                )}
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div>
                    <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">{followerLabel}</p>
                    <p className="text-xl font-black text-white tracking-tight">{formatNum(followerCount)}</p>
                </div>
                <div>
                    <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">Posts</p>
                    <p className="text-xl font-black text-white tracking-tight">{formatNum(entry.posts)}</p>
                </div>
                <div>
                    <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold mb-1">Views</p>
                    <p className="text-xl font-black text-white tracking-tight">{formatNum(entry.views)}</p>
                </div>
            </div>
        </GlassCard>
    );
};

export const MainPageStats = () => {
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl">
                <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">Main Page</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">
                    @themaskwithabhiveer — Flagship
                </h2>
                <p className="text-white/50 text-center mb-12 max-w-xl mx-auto">
                    Per-platform performance for the main page over the full {WINDOW.days}-day window.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <PlatformCard entry={MAIN.ig} followerLabel="Followers" />
                    <PlatformCard entry={MAIN.yt} followerLabel="Subscribers" />
                </div>

                {MAIN.tt.status === 'pending' ? (
                    <div className="mt-6 flex items-center justify-center gap-3 text-sm text-white/50">
                        <div className={`${platformAccent('TikTok')} p-2 rounded-lg inline-flex`}>
                            <Music2 size={14} />
                        </div>
                        <span>
                            <span className="text-white/70 font-semibold">TikTok ({MAIN.tt.handle})</span>
                            <span className="mx-2 text-white/30">·</span>
                            <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 text-xs font-bold px-2 py-0.5 rounded-full">
                                Data pending
                            </span>
                        </span>
                    </div>
                ) : (
                    <div className="mt-6">
                        <PlatformCard entry={MAIN.tt} followerLabel="Followers" />
                    </div>
                )}
            </div>
        </section>
    );
};
